import { useCallback, useEffect, useMemo, useState } from 'react'
import { Accordion, Button, Form, InputGroup } from 'react-bootstrap'
import PlusSvg from '../../../common/components/svg/PlusSvg'
import { DateTime } from 'luxon'
import CustomError, { CustomErrorProps } from '../../../common/components/CustomError'
import { FileUploader } from 'react-drag-drop-files'
import { CloudUpload, Save } from 'react-bootstrap-icons'

interface WootFbaNewJourneyProps {
  handleNewJourneySave: any
}

export default function WootFbaNewJourney(props: WootFbaNewJourneyProps){
  const [poNumber, setPoNumber] = useState(undefined)
  const [pdfFile, setPdfFile] = useState(undefined)
  const [excelFile, setExcelFile] = useState(undefined)
  const [iagShipmentName, setIagShipmentName] = useState(undefined)
  const [journeyError, setJourneyError] = useState(undefined)
  const [errors, setErrors] = useState([])

  useEffect(() => {
    if(!poNumber) {
      return
    }
    const today = DateTime.now().setZone('America/Chicago')
    setIagShipmentName(`Woot FBA Flip Allocation ${poNumber} ${today.month}.${today.day}.${today.year} Outdoor Decor FBA`)
  }, [poNumber])

  const handleSaveClicked = useCallback(async (e) => {
    setJourneyError(undefined)
    const errors = []
    if(!poNumber){
      errors.push('Invalid PO Number.')
    }
    if(!pdfFile){
      errors.push('Original PDF upload is missing.')
    }
    if(!excelFile){
      errors.push('Original Excel upload is missing.')
    }
    setErrors(errors)
    if(errors.length > 0){
      
      const message = errors.map(e => (
        <><span>{e} </span></>
      ))
      setJourneyError({
        content: message,
        description: message,
        title: 'Journey Validation Errors',
        visible: true
      } as CustomErrorProps)
      return
    }

    const response = {
      poNumber,
      iagShipmentName,
      pdfFile,
      excelFile
    }
    await props.handleNewJourneySave(response)
    setPoNumber(undefined)
    setExcelFile(undefined)
    setPdfFile(undefined)
    setJourneyError(undefined)

  }, [poNumber, pdfFile, excelFile, iagShipmentName])

  const handlePoNumberChanged = useCallback((e) => {
    setJourneyError(undefined)

    if(e.currentTarget.value.trim().length === 0){
      setJourneyError({
        content: 'PO Number is required.',
        description: 'PO Number is required.',
        title: 'Journey Validation Errors',
        visible: true
      } as CustomErrorProps)
      setPoNumber('')
      return
    }

    setPoNumber(e.currentTarget?.value?.toUpperCase())

  }, [])

  const handlePdfFileUpload = useCallback((file: File) => {
    setPdfFile(file)
  }, [])

  const handleExcelFileUpload = useCallback((file: File) => {
    setExcelFile(file)
  }, [])

  const handleCloseJourneyError = useCallback((e) => {
    setJourneyError({
      visible: false
    })
  }, [])

  const view = useMemo(() => {
    return <Accordion className='custom-accordion'>
    <Accordion.Item eventKey='0'>
      <Accordion.Header><span className='beacon-title'><PlusSvg height={20}/> NEW JOURNEY</span></Accordion.Header>
      <Accordion.Body>

        <div>
          <div>
            <Button onClick={handleSaveClicked} variant='success' style={{fontSize: '14px'}}><Save/>&nbsp;&nbsp;SAVE NEW JOURNEY</Button>
            <hr/>
            <div style={{width: '50%'}}>
              <div className='beacon-header'>DETAILS</div>
              <hr/>
              <span className='journey-subtext' style={{color: errors.includes('Invalid PO Number.') ? 'darkred' : ''}}>PO NUMBER</span>
              <Form.Control 
                required 
                onChange={handlePoNumberChanged} 
                value={poNumber ?? ''} 
                placeholder='eg. PO147786' 
                type='text' 
                maxLength={255}
                style={{borderColor: errors.includes('Invalid PO Number.') ? 'darkred' : ''}}
              />

              <span className='beacon-label'>
                {poNumber ? iagShipmentName : ''} # of #
              </span>
            </div>
            <div style={{width: '50%'}}>
              <span className='beacon-header'>UPLOAD FILES</span>
              <hr/>
              <div style={{display: 'flex', flexWrap: 'wrap'}}>
                {!pdfFile && <div>
                    <FileUploader 
                        handleChange={handlePdfFileUpload} 
                        name="file" 
                        types={['pdf']}
                      >
                        <div className='journey-step warning'>
                          <div className="journey-title">UPLOAD PDF</div>
                          <CloudUpload height={50} width={50} />
                          <div className="journey-subtext">Drag & Drop / Click</div>
                        </div>

                      </FileUploader>
                  </div>
                }
                {pdfFile && <div>
                  <FileUploader 
                      handleChange={handlePdfFileUpload} 
                      name="file" 
                      types={['pdf']}
                    >
                      <div className='journey-step success'>
                        <div className="journey-title">UPLOAD PDF</div>
                        <CloudUpload height={50} width={50} />
                        <div className="journey-subtext">{pdfFile.name}</div>
                      </div>

                    </FileUploader>
                </div>
                }
                {!excelFile && <div>
                    <FileUploader 
                        handleChange={handleExcelFileUpload} 
                        name="file" 
                        types={['xlsx']}
                      >
                        <div className='journey-step warning'>
                          <div className="journey-title">UPLOAD EXCEL</div>
                          <CloudUpload height={50} width={50} />
                          <div className="journey-subtext">Drag & Drop / Click</div>
                        </div>
                      </FileUploader>
                  </div>
                }
                {excelFile && <div>
                    <FileUploader 
                        handleChange={handleExcelFileUpload} 
                        name="file" 
                        types={['xlsx']}
                      >
                        <div className='journey-step success'>
                          <div className="journey-title">UPLOAD EXCEL</div>
                          <CloudUpload height={50} width={50} />
                          <div className="journey-subtext">{excelFile.name}</div>
                        </div>
                      </FileUploader>
                  </div>
                }
              </div>
            </div>
          </div>          
          <CustomError closeError={handleCloseJourneyError} content={journeyError?.content} description={journeyError?.description} title={journeyError?.title} visible={journeyError?.visible} />
          
        </div>
      </Accordion.Body>
    </Accordion.Item>
  </Accordion>
  }, [poNumber, iagShipmentName, journeyError, pdfFile, excelFile, errors])

  return view
}